import { UserInfo } from './AISystems.types';

export type Status = 'PENDING' | 'ACCEPTED' | 'EXPIRED';

export type Role = 'ADMIN' | 'EDITOR' | 'VIEWER' | 'OWNER';
export type PlatformRole = 'SUPER_ADMIN' | 'ADMIN' | 'USER';

export type TeamRolesDTO = {
  aiSystemRoles: Role[];
  platformRoles: PlatformRole[];
};

export type TeamUsersDTO = {
  id: string;
  email: string;
  name: string;
};

export type ResendInvitationDTO = {
  message: string;
  expiresAt: string;
};

export type PlatformInvitationDTO = {
  invitationId: string;
  email: string;
  roleCreated: PlatformRole;
  expiresAt: string;
  invitationUrl: string;
};

export type OrganizationRef = {
  id: string;
  name: string;
  // The stable identity. `name` is editable by the organization's admin; `slug`
  // is the Keycloak group path and never changes. Key off id or slug, show name.
  slug: string;
};

/**
 * One row of GET /users. Registered users and pending invitations arrive in the
 * same collection so the grid can sort and page across both server-side.
 */
export type UserListItem = {
  type: 'REGISTERED' | 'INVITED';
  // On an INVITED row this is an *invitation* id, not a user id. Every row
  // action accepts either, so pass it through untouched — but never assume it
  // resolves to a user.
  userId: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  platformRole: PlatformRole;
  // Null only for a user genuinely without an organization — someone who
  // accepted an AI-system-only invitation.
  organization: OrganizationRef | null;
  // For a USER caller this is the count of systems shared with them, not the
  // row's real total: publishing the total would disclose systems the caller
  // cannot see. The same person can therefore show a different number to
  // different viewers, by design.
  assignedAiSystems: number;
  lastActive: string | null;
  createdAt: string;
  expiresAt: string | null;
  // Computed from expiresAt at read time. The job that flips PENDING to EXPIRED
  // in the database runs weekly, so this is the only trustworthy source.
  invitationStatus: Status | null;
  invitedBy: { id: string; email: string; name: string } | null;
};

export type UsersListDTO = {
  items: UserListItem[];
  count: number;
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
  nextOffset: number | null;
};

// The server rejects anything outside this set with a 400 rather than silently
// returning unsorted rows, so never forward a raw column key.
export type UsersSortBy =
  | 'name'
  | 'email'
  | 'lastActive'
  | 'assignedAiSystems'
  | 'organization'
  | 'platformRole'
  | 'status'
  | 'createdAt';

export const USERS_SORT_KEYS: UsersSortBy[] = [
  'name',
  'email',
  'lastActive',
  'assignedAiSystems',
  'organization',
  'platformRole',
  'status',
  'createdAt'
];

export type GetUsersParams = {
  q?: string;
  type?: 'REGISTERED' | 'INVITED' | 'ALL';
  // Describes invitations only, so sending it excludes registered rows.
  status?: 'PENDING' | 'EXPIRED';
  platformRole?: 'ADMIN' | 'USER';
  organizationId?: string[];
  sortBy?: UsersSortBy;
  order?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
};

export type InvitationAcceptanceRequest = {
  token: string;
  email: string;
  password: string;
  firstName: string;
  lastName: string;
};

export type InvitationActivationDTO = {
  message: string;
  user: UserInfo;
  nextStep: 'oidc_login';
};

export type InvitationAcceptanceDTO = {
  message: string;
  user: UserInfo;
  // No tokens are issued: the invitee signs in via Keycloak with the password
  // they just chose.
  nextStep?: 'oidc_login';
};

export type CurrentUser = {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  name: string;
  assignedAiSystems: number;
  isRegistered: boolean;
  role: PlatformRole;
  invitationStatus: Status | null;
};

export type AiSystem = {
  aiSystemId: string;
  aiSystemName: string;
  role: Role;
  joinedAt: string;
};

export type AiSystemsForUserDTO = {
  items: AiSystem[];
  count: number;
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
  nextOffset: number | null;
};

export type UpdateUserRoleRequest = {
  updates: {
    aiSystemId: string;
    role: Role;
  }[];
};

// PATCH /users/{id}/platform-role moves a user between these two only.
// SUPER_ADMIN is not an organization role, so it is rejected at the DTO with a
// 400 listing the legal values rather than a 403 from deeper in the service.
export type AssignablePlatformRole = Extract<PlatformRole, 'ADMIN' | 'USER'>;

export type UpdatePlatformRoleRequest = {
  role: AssignablePlatformRole;
};

export type ChangePlatformRoleDTO = {
  message: string;
  role: AssignablePlatformRole;
  // False when the user already held the requested role. Not an error: the
  // commonest way to reach it is a client retrying a request that committed.
  changed: boolean;
};

type ErrorCode =
  | 'TEAM_INVITATION_INVALID_TOKEN'
  | 'TEAM_INVITATION_INVALID_TOKEN_TYPE'
  | 'TEAM_INVITATION_EXPIRED'
  | 'TEAM_INVITATION_ALREADY_ACCEPTED';

export type ValidateInvitationResponse = {
  valid: boolean;
  requiresAccountCreation: boolean;
  // OIDC mode only: the invited email already has a Keycloak identity, so the
  // invitation is activated against it instead of through the registration form.
  keycloakUserExists?: boolean;
  // The organization being joined, and the platform role granted. Both are
  // absent for AI-system invitations.
  organizationName?: string;
  role?: PlatformRole;
  systems: AiSystem[];
  inviterName: string;
  expiresAt: string;
  // Present only when `valid` is false.
  errorCode?: ErrorCode;
  message?: string;
};
